/* eslint-disable react-hooks/preserve-manual-memoization */
import { CloseIcon } from '@/Components/Common/Icons';
import {
  convertTemperature,
  getSummaryData,
  getTempHumiSummaryData,
  onTrendIconClick,
} from '@/Components/Environmental/EnvironmentalUtils';
import TrendChart from '@/Components/Environmental/TrendChart';
import { useUserDeviceEventsStream } from '@/api';
import { get_utc_offset } from '@/Components/MotionActivity/MotionActivityUtils';
import {
  convertEventsToSegments,
  roundUpToNextHour,
  SMARTHOME_HISTORY_DAYS,
} from '@/Components/Sensors/SensorUtils';
import { SensorTable } from '@/Components/Sensors/Table/SensorTable';
import {
  COOKTOP_ALERTS_TAB,
  DEVICE_EVENT_TYPES_CHARTS,
  DEVICE_EVENTS_PROPERTY,
  EVENT_TYPES,
  SENSORS,
} from '@/constants/device';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { AppStorageKeys, DefaultTimezone } from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import LocalStorage from '@vantiva-sa/homesight_humaui/Utilities/LocalStorage';
import { Alert, Modal, Spin, Tabs } from 'antd';
import { groupBy, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import DoorWindowChart from './DoorWindowChart';
import './SensorChartModal.less';

const ModalWrapper = styled(Modal)`
  .grey-value {
    width: 200px;
  }
  .tab-content {
    margin: 0;
  }
  .sensor-table-tabs {
    margin-top: 10px;
    .ant-tabs-content {
      margin-top: 20px;
    }
  }
  .sensor-state-title {
    font-size: 14px;
  }
  &.environmental_sensor,
  &.temperature_sensor {
    .ant-modal-content {
      height: 1049px;
    }
    .ant-tabs-content-holder {
      height: 848px;
    }
    .history-events-container {
      height: 772px;
    }
  }
  &.occupancy_sensor {
    .modal-wellness-item-content-statistic-info {
      width: 100px;
      margin-left: 15px;
      min-width: unset;
    }
    .modal-wellness-item-content-statistic-info-value {
      right: unset !important;
      .sensor-state {
        right: unset;
        width: 48px;
        height: 43px;
        path {
          fill: #005d98;
          stroke: #005d98;
        }
      }
    }
    .card-today-value {
      display: flex;
      align-items: center;
    }
    .sensor-state-title {
      color: #005d98;
    }
  }
  &.cooktop_sensor {
    .modal-wellness-item-content-statistic-info {
      width: 130px;
      margin-left: 15px;
      min-width: unset;
    }
    .modal-wellness-item-content-statistic-info-value {
      right: unset !important;
      .sensor-state {
        right: unset;
        width: 48px;
        height: 43px;
        path {
          fill: #005d98;
          stroke: #005d98;
        }
      }
    }
    .card-today-value {
      display: flex;
      align-items: center;
    }
    .sensor-state-title {
      color: #005d98;
    }
  }
  &.radar_sensor {
    .modal-wellness-item-content-statistic-info {
      width: 130px;
      margin-left: 15px;
      min-width: unset;
    }
    .modal-wellness-item-content-statistic-info-value {
      right: unset !important;
      .sensor-state {
        right: unset;
        width: 48px;
        height: 43px;
        path {
          fill: #005d98;
          stroke: #005d98;
        }
      }
    }
    .card-today-value {
      display: flex;
      align-items: center;
    }
    .sensor-state-title {
      color: #005d98;
    }
    .radar-legend {
      flex-wrap: wrap;
      max-width: 560px;
      row-gap: 6px;
    }
  }
  .modal-wellness-item-content-header {
    width: 250px;
    gap: 57px;
  }
  .modal-wellness-item-content-statistic {
    width: 100px;
    min-width: unset;
  }
  .ant-modal-content {
    height: 738px;
    width: 1274px;
  }
  .spinner-container {
    height: 477px;
    justify-content: center;
    display: flex;
    align-items: center;
  }
  .table-stream-spinner {
    display: flex;
    justify-content: center;
    padding: 12px 0;
  }
  .trend-chart-container {
    width: 100%;
    .graph-location-container {
      min-width: unset;
    }
  }
  .ant-tabs-content-holder {
    height: 583px;
  }
`;

const getDefaultRangeDays = ({ deviceType }) => {
  switch (deviceType) {
    case SENSORS.SMART_HOME.TEMPERATURE_HUMIDITY_SENSOR:
    case SENSORS.SMART_HOME.ENVIRONMENTAL_SENSOR:
      return 30;
    default:
      return 7;
  }
};

const TIME_RANGES = {
  TODAY: 'today',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
};

const MONTHLY_RANGE_DAYS = 30;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const MODAL_TABS = { CHART: '1', TABLE: '2' };

// Splits the events into the Table tab's sub-tabs (one per event type / cooktop state).
const groupTableEvents = (events, deviceType) => {
  if (!events.length) return [];

  if (deviceType === SENSORS.SMART_HOME.COOKTOP_SENSOR) {
    const alertEventTypes = [
      EVENT_TYPES.COOKTOP_SENSOR.EXPOSED_BURNER,
      EVENT_TYPES.COOKTOP_SENSOR.POT_TOO_HOT,
      EVENT_TYPES.COOKTOP_SENSOR.UNATTENDED,
    ];
    const presenceEvents = events.filter((e) =>
      e.type?.startsWith(EVENT_TYPES.COOKTOP_SENSOR.PRESENCE + '.')
    );
    const cookingEvents = events.filter((e) =>
      e.type?.startsWith(EVENT_TYPES.COOKTOP_SENSOR.ACTIVE_COOKING + '.')
    );
    const alertEvents = events.filter((e) =>
      alertEventTypes.some((at) => e.type?.startsWith(at + '.'))
    );
    return [
      ...(presenceEvents.length
        ? [{ type: EVENT_TYPES.COOKTOP_SENSOR.PRESENCE, events: presenceEvents }]
        : []),
      ...(cookingEvents.length
        ? [{ type: EVENT_TYPES.COOKTOP_SENSOR.ACTIVE_COOKING, events: cookingEvents }]
        : []),
      ...(alertEvents.length ? [{ type: COOKTOP_ALERTS_TAB, events: alertEvents }] : []),
    ];
  }

  if (deviceType === SENSORS.SMART_HOME.TOILET_FLUSH_SENSOR) {
    return [
      {
        type: EVENT_TYPES.FLUSH_SENSOR.ACCESSORY_STATE_UPDATE_TOILET_FLUSH_SENSOR_ACTIVE,
        events: [...events].sort(
          (a, b) =>
            new Date(b.recv_time ?? b.utc_timestamp_inms) -
            new Date(a.recv_time ?? a.utc_timestamp_inms)
        ),
      },
    ];
  }

  const entries = Object.entries(groupBy(events, 'message_type'));
  const groups = entries.map(([type, ev]) => ({ type, events: ev }));
  if (deviceType === SENSORS.SMART_HOME.TEMPERATURE_HUMIDITY_SENSOR) {
    // Since there is no separate eventType for humidity,
    // need to create a separate entry for humidity table
    return groups.concat(
      entries.map(([, ev]) => ({ type: DEVICE_EVENTS_PROPERTY.humidity, events: ev }))
    );
  }
  return groups;
};

const SensorChartModal = ({
  open,
  onClose,
  sensorInfoIcon,
  hideSensorIcon = false,
  userTempUnit,
  useruuid,
  timezone,
  timeFormat,
  loginUserLocale,
  sensorDetails,
  deviceType,
  setSelectedSensorDetails,
}) => {
  const [selectedRange, setSelectedRange] = useState(TIME_RANGES.WEEKLY);
  const [activeTab, setActiveTab] = useState(MODAL_TABS.CHART);
  const [summaryData, setSummaryData] = useState({});
  const selectedLanguage = LocalStorage.get(AppStorageKeys.language) || 'en';

  const getDaysForRange = (selRange) => {
    if (selRange === TIME_RANGES.WEEKLY) return 7;
    if (selRange === TIME_RANGES.MONTHLY) return MONTHLY_RANGE_DAYS;
    if (selRange === TIME_RANGES.TODAY) return 1;
    return getDefaultRangeDays({ deviceType });
  };

  const getInitialDates = useCallback(() => {
    if (
      deviceType === SENSORS.SMART_HOME.TEMPERATURE_HUMIDITY_SENSOR ||
      deviceType === SENSORS.SMART_HOME.ENVIRONMENTAL_SENSOR
    ) {
      const now = new Date();
      const windowEnd = roundUpToNextHour(now);
      // One fetch feeds both the chart and the history table: cover the history window, and at
      // least a full month so the Month chart is complete.
      const days = Math.max(SMARTHOME_HISTORY_DAYS, MONTHLY_RANGE_DAYS);
      const MS_PER_DAY = 24 * 60 * 60 * 1000;
      const windowStart = new Date(windowEnd.getTime() - days * MS_PER_DAY);
      return { start: windowStart.toISOString(), end: windowEnd.toISOString() }; // for environmental sensors and temp and humi sensors, always fetch whole monthly data.
    } else {
      const now = new Date();
      const windowEnd = roundUpToNextHour(now);
      const days = 7;
      const MS_PER_DAY = 24 * 60 * 60 * 1000;
      const windowStart = new Date(windowEnd.getTime() - days * MS_PER_DAY);
      return { start: windowStart.toISOString(), end: windowEnd.toISOString() };
    }
  }, [deviceType, sensorDetails?.deviceUuid]);

  const initialDates = getInitialDates();

  const MS_PER_HOUR = 60 * 60 * 1000;
  const getWindowByRange = useCallback(
    (selectedRange) => {
      const rawNow = new Date();
      const windowEnd = roundUpToNextHour(rawNow);
      const hours = selectedRange === 1 ? 24 : selectedRange === 7 ? 24 * 7 : 24 * 30;
      const windowStart = new Date(windowEnd.getTime() - hours * MS_PER_HOUR);
      return { windowStart, windowEnd };
    },
    [selectedRange]
  );
  const trans = useSelector(translator);

  const [endDate, setEndDate] = useState(initialDates.end);
  const [startDate, setStartDate] = useState(initialDates.start);
  const eventTypes = DEVICE_EVENT_TYPES_CHARTS[deviceType] || [];
  const [segments, setSegments] = useState([]);
  const [cookingSegments, setCookingSegments] = useState([]);
  const [view, setView] = useState('temperature');
  const [pastDataProcessed, setPastDataProcessed] = useState({});

  const userEventsApi = useSelector((state) => state.apis.userEventsApi);
  const weatherApi = useSelector((state) => state.apis.weatherApi);

  const timezoneOffset = useMemo(() => get_utc_offset(timezone, DefaultTimezone), [timezone]);
  const [indoorTrendDataLoader, setIndoorTrendDataLoader] = useState(false);
  const [outdoorTrendDataLoader, setOutdoorTrendDataLoader] = useState(false);
  const [graphModalTitle, setGraphModalTitle] = useState('');
  const [modalTitleDeviceLocationObj, setModalTitleDeviceLocationObj] = useState({});

  const {
    data: streamedEvents,
    isLoading: isTableLoading,
    isStreaming: isTableStreaming,
    isPartial: isTablePartial,
    isFetching,
    isStreamComplete,
    refetch: refetchUserEvents,
  } = useUserDeviceEventsStream({
    useruuid,
    deviceuuid: sensorDetails?.deviceuuid,
    eventTypes,
    startDate,
    endDate,
    enabled: !!open,
    // Keep every event the API returns; they are split per type for the table and charts below.
    filter: null,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  // The Table tab renders rows as they stream in. Charts, summaries and trends only get the
  // complete result, so their data points are never drawn from a partial download.
  const userEvents = isStreamComplete ? streamedEvents : undefined;

  // Temperature/environmental sensors fetch at least a month for the Month chart; their table only
  // shows the configured history window, ending at the same time as the request.
  const groupedUserEvents = useMemo(() => {
    const isMonthlyFetchSensor =
      deviceType === SENSORS.SMART_HOME.TEMPERATURE_HUMIDITY_SENSOR ||
      deviceType === SENSORS.SMART_HOME.ENVIRONMENTAL_SENSOR;
    const historyStart = new Date(endDate).getTime() - SMARTHOME_HISTORY_DAYS * MS_PER_DAY;
    const tableEvents = isMonthlyFetchSensor
      ? streamedEvents.filter((e) => new Date(e.utc_timestamp_inms).getTime() >= historyStart)
      : streamedEvents;
    return groupTableEvents(tableEvents, deviceType);
  }, [streamedEvents, deviceType, endDate]);

  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    if (
      deviceType &&
      deviceType !== SENSORS.SMART_HOME.TEMPERATURE_HUMIDITY_SENSOR &&
      deviceType !== SENSORS.SMART_HOME.ENVIRONMENTAL_SENSOR
    ) {
      const now = new Date();
      const windowEnd = roundUpToNextHour(now);
      // The Table tab shows the configured history window; the Chart tab follows its range selector.
      const days =
        activeTab === MODAL_TABS.TABLE ? SMARTHOME_HISTORY_DAYS : getDaysForRange(selectedRange); //when ever range changes in graph this will be executed. this is needed only for senors that are not temp and humi sensor and environmental sensor. this will cause data fetch from network whenever range changes.
      const MS_PER_DAY = 24 * 60 * 60 * 1000;
      const windowStart = new Date(windowEnd.getTime() - days * MS_PER_DAY);
      setStartDate(windowStart.toISOString());
      setEndDate(windowEnd.toISOString());
    }
  }, [selectedRange, deviceType, activeTab]);

  useEffect(() => {
    if (isEmpty(sensorDetails)) {
      setSummaryData({});
    }
  }, [sensorDetails]);

  useEffect(() => {
    if (userEvents && Array.isArray(userEvents) && useruuid) {
      if (deviceType === SENSORS.SMART_HOME.ENVIRONMENTAL_SENSOR) {
        const summary = getSummaryData({
          userEvents,
          deviceUuid: sensorDetails?.deviceuuid,
          selectedRange,
          timezone,
          loginUserTimeformat: timeFormat,
          trans,
          displayTempUnit: userTempUnit,
        });
        setSummaryData(summary);
      }
      if (deviceType === SENSORS.SMART_HOME.TEMPERATURE_HUMIDITY_SENSOR) {
        const summary = getTempHumiSummaryData({
          userEvents,
          deviceUuid: sensorDetails?.deviceuuid,
          selectedRange,
          timezone,
          displayTempUnit: userTempUnit,
        });
        setSummaryData(summary);
      }
    }
  }, [userEvents, useruuid, selectedRange]);

  useEffect(() => {
    if (!open) {
      setStartDate(null);
      setEndDate(null);
      setSelectedSensorDetails({}); //always reset when popup modal is closed
      setSelectedRange(TIME_RANGES.WEEKLY);
      setActiveTab(MODAL_TABS.CHART);
    } else if (
      deviceType &&
      (deviceType === SENSORS.SMART_HOME.TEMPERATURE_HUMIDITY_SENSOR ||
        deviceType === SENSORS.SMART_HOME.ENVIRONMENTAL_SENSOR)
    ) {
      const { start, end } = getInitialDates(); //set initial dates when modal opens
      setStartDate(start);
      setEndDate(end);
    }
  }, [open]);

  useEffect(() => {
    if (userEvents && useruuid) {
      if (
        deviceType === SENSORS.SMART_HOME.ENVIRONMENTAL_SENSOR ||
        deviceType === SENSORS.SMART_HOME.TEMPERATURE_HUMIDITY_SENSOR
      ) {
        onTrendIconClick(
          useruuid,
          sensorDetails?.deviceuuid,
          userEventsApi,
          weatherApi,
          trans,
          setPastDataProcessed,
          timezoneOffset,
          () => ({}),
          selectedRange,
          convertTemperature,
          userTempUnit,
          view,
          setOutdoorTrendDataLoader,
          setIndoorTrendDataLoader,
          location,
          () => ({}),
          sensorDetails?.deviceuuid,
          () => ({}),
          null,
          null,
          null,
          () => ({}),
          () => ({}),
          userEvents,
          sensorDetails
        );
      }
    }
  }, [userEvents, useruuid]);

  useEffect(() => {
    if (open) {
      setSelectedRange(TIME_RANGES.WEEKLY);
    }
  }, [open, deviceType]);

  // Chart segments (complete data only).
  useEffect(() => {
    if (userEvents?.length > 0) {
      if (deviceType === SENSORS.SMART_HOME.COOKTOP_SENSOR) {
        const cookingEvents = userEvents.filter((e) =>
          e.type?.startsWith(EVENT_TYPES.COOKTOP_SENSOR.ACTIVE_COOKING + '.')
        );

        // Compute cooking segments for the second chart row
        const { windowStart, windowEnd } = getWindowByRange(selectedRange);
        const sortedCooking = [...cookingEvents].sort(
          (a, b) => new Date(a.recv_time) - new Date(b.recv_time)
        );
        const cSegs = [];
        for (let i = 0; i < sortedCooking.length - 1; i++) {
          const cur = sortedCooking[i];
          const nxt = sortedCooking[i + 1];
          const start = new Date(cur.recv_time);
          const end = new Date(nxt.recv_time);
          if (start > windowEnd || end < windowStart) continue;
          const cs = start < windowStart ? windowStart : start;
          const ce = end > windowEnd ? windowEnd : end;
          if (ce <= cs) continue;
          cSegs.push({
            startTs: cs.toISOString(),
            endTs: ce.toISOString(),
            state: cur.iseventdetected ? 'cooking_active' : 'cooking_inactive',
          });
        }
        // If last cooking event is still active, extend to windowEnd
        if (sortedCooking.length > 0) {
          const last = sortedCooking[sortedCooking.length - 1];
          if (last.iseventdetected) {
            const start = new Date(last.recv_time);
            if (start <= windowEnd) {
              const cs = start < windowStart ? windowStart : start;
              cSegs.push({
                startTs: cs.toISOString(),
                endTs: windowEnd.toISOString(),
                state: 'cooking_active',
              });
            }
          }
        }
        setCookingSegments(cSegs);
      }

      const { windowStart, windowEnd } = getWindowByRange(selectedRange);

      const result = convertEventsToSegments(userEvents, {
        windowStart,
        windowEnd,
        sensorDetails,
      });

      setSegments(result);
    } else {
      setCookingSegments([]);
    }
  }, [userEvents, deviceType, selectedRange]);

  const handleTabChange = (activeKey) => {
    setActiveTab(activeKey);
    if (activeKey === MODAL_TABS.TABLE) {
      setSelectedRange(TIME_RANGES.MONTHLY);
      refetchUserEvents();
    } else {
      setSelectedRange(TIME_RANGES.WEEKLY);
    }
  };

  return (
    <ModalWrapper
      open={open}
      onCancel={onClose}
      className={`sensor-chart-modal ${deviceType || ''}`}
      closeIcon={<CloseIcon className="close-icon" onClick={onClose} />}
      destroyOnHidden
      height={738}
      width={1274}
      footer={null}
    >
      {!hideSensorIcon && (
        <div className="sensor-chart-modal__header">
          <div>{sensorInfoIcon}</div>
        </div>
      )}
      <div className="sensor-chart-modal__body">
        <div className="tabs-container">
          <Tabs
            defaultActiveKey={MODAL_TABS.CHART}
            className="env-tabs"
            tabPosition="top"
            onChange={handleTabChange}
            items={[
              {
                label: 'Chart',
                key: MODAL_TABS.CHART,
                children:
                  !userEvents || isFetching ? (
                    <div className="spinner-container">
                      <Spin spinning={true}></Spin>
                    </div>
                  ) : (
                    <div className="chart-container">
                      {(deviceType === SENSORS.SMART_HOME.DOOR_WINDOW_SENSOR ||
                        deviceType === SENSORS.SMART_HOME.TOILET_FLUSH_SENSOR ||
                        deviceType === SENSORS.SMART_HOME.SMART_PLUG ||
                        deviceType === SENSORS.SMART_HOME.OCCUPANCY_SENSOR ||
                        deviceType === SENSORS.SMART_HOME.RADAR_SENSOR ||
                        deviceType === SENSORS.SMART_HOME.COOKTOP_SENSOR) && (
                        <DoorWindowChart
                          segments={segments}
                          cookingSegments={cookingSegments}
                          selectedRange={selectedRange}
                          setSelectedRange={setSelectedRange}
                          timezone={timezone}
                          is24hoursformat={timeFormat?.is24hoursformat}
                          loginUserLocale={loginUserLocale}
                          sensorDetails={sensorDetails}
                        />
                      )}
                      {(sensorDetails?.devicetype === SENSORS.SMART_HOME.ENVIRONMENTAL_SENSOR ||
                        sensorDetails?.devicetype ===
                          SENSORS.SMART_HOME.TEMPERATURE_HUMIDITY_SENSOR) && (
                        <>
                          {!indoorTrendDataLoader && !outdoorTrendDataLoader && !isFetching ? (
                            <TrendChart
                              key={selectedRange}
                              outdoorData={pastDataProcessed.outdoorDataFetched}
                              indoorData={pastDataProcessed.trendDataFetched}
                              deviceUuidToShowTrend={sensorDetails?.deviceuuid}
                              tempUnit={userTempUnit}
                              location={sensorDetails?.location}
                              loginUserLocale={loginUserLocale}
                              loginUserTimeformat={timeFormat}
                              selectedRange={selectedRange}
                              setSelectedRange={setSelectedRange}
                              timezone={timezone}
                              view={view}
                              setViewHandler={setView}
                              indoorTrendDataLoader={indoorTrendDataLoader}
                              outdoorTrendDataLoader={outdoorTrendDataLoader}
                              timezoneOffset={timezoneOffset}
                              summaryData={summaryData}
                              hideAmbientLight={
                                sensorDetails?.devicetype ===
                                SENSORS.SMART_HOME.TEMPERATURE_HUMIDITY_SENSOR
                              }
                            />
                          ) : (
                            <div className="spinner-container">
                              <Spin spinning={true}></Spin>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ),
              },
              {
                label: 'Table',
                key: MODAL_TABS.TABLE,
                children: (
                  <>
                    {isTableLoading ? (
                      <div className="spinner-container">
                        <Spin spinning={true}></Spin>
                      </div>
                    ) : (
                      <>
                        <SensorTable
                          timezone={timezone}
                          userTempUnit={userTempUnit}
                          loginUserLocale={loginUserLocale}
                          selectedLanguage={selectedLanguage}
                          groupedUserEvents={groupedUserEvents}
                          sensorDetails={sensorDetails}
                          loginUserTimeformat={timeFormat}
                        />
                        {/* Rows are still streaming in. */}
                        {isTableStreaming && <Spin size="small" className="table-stream-spinner" />}
                        {/* The download failed part-way: the rows above are incomplete. */}
                        {isTablePartial && (
                          <Alert type="warning" showIcon message={trans('ERROR_MESSAGE')} />
                        )}
                      </>
                    )}
                  </>
                ),
              },
            ]}
          />
        </div>
      </div>
    </ModalWrapper>
  );
};

SensorChartModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  sensorInfoIcon: PropTypes.node,
  hideSensorIcon: PropTypes.bool,
  userTempUnit: PropTypes.string,
  useruuid: PropTypes.string.isRequired,
  timezone: PropTypes.string,
  timeFormat: PropTypes.shape({
    is24hoursformat: PropTypes.bool.isRequired,
  }).isRequired,
  loginUserLocale: PropTypes.string,
  sensorDetails: PropTypes.object,
  deviceType: PropTypes.string,
  setSelectedSensorDetails: PropTypes.func.isRequired,
};

export default SensorChartModal;
